/**
 * ModularLayout - Layout unificado que renderiza sidebar baseada no módulo ativo
 * 
 * Este layout é usado para rotas compartilhadas entre módulos (Contatos, Listas)
 * permitindo que a estrutura visual (sidebar, header) corresponda ao módulo
 * de onde o usuário veio.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { ReactNode, useState } from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
// Navegação 2.0 — rail de categorias + painel de submenus (substitui sidebars por módulo)
import { AppShellSidebar } from '@/components/navigation/AppShellSidebar';
import { useAuth } from '@/hooks/useAuth';
import { useHeaderModuleFlags } from '@/hooks/useHeaderModuleFlags';
import { ThemeToggle } from '@/components/ui/theme-toggle';

import { TooltipProvider } from '@/components/ui/tooltip';
import { UserProfileDialog } from '@/components/tenant/UserProfileDialog';
import { TenantSwitcher } from '@/components/tenant/TenantSwitcher';
import { TrialBanner } from '@/components/tenant/TrialBanner';
// Hook do novo modelo de planos (integracao_v1) — suprime banner legado
// quando o tenant já possui binding na nova estrutura.
import { useTenantBillingSelfOverview } from '@/hooks/tenant/useTenantPlansV2';

import { NotificationBell } from '@/components/tenant/notifications/NotificationBell';
import { AppLauncherButton } from '@/components/shortcuts/AppLauncherButton';
import { QuickCreateButton } from '@/components/quick-create/QuickCreateButton';
import { DisconnectedInboxesIndicator } from '@/components/tenant/inbox/DisconnectedInboxesIndicator';
import { HeaderOverflowMenu } from '@/components/tenant/HeaderOverflowMenu';

import { UserMenu } from '@/components/tenant/UserMenu';
// Pesquisa global do header (contatos + clientes)
import { HeaderSearchSlot } from '@/components/layouts/HeaderSearchSlot';

import { PaymentGuard } from '@/components/guards/PaymentGuard';

interface ModularLayoutProps {
  children: ReactNode;
}

/**
 * Obtém a role principal do usuário no tenant atual
 */
const getUserRole = (roles: { role: string; tenant_id: string | null }[], tenantId?: string): 'root_admin' | 'tenant_admin' | 'tenant_supervisor' | 'agent' | 'user' => {
  if (!tenantId) return 'user';
  
  // Prioridade: root_admin > tenant_admin > tenant_supervisor > agent > user
  if (roles.some(r => r.role === 'root_admin' && r.tenant_id === null)) return 'root_admin';
  if (roles.some(r => r.role === 'tenant_admin' && r.tenant_id === tenantId)) return 'tenant_admin';
  if (roles.some(r => r.role === 'tenant_supervisor' && r.tenant_id === tenantId)) return 'tenant_supervisor';
  if (roles.some(r => r.role === 'agent' && r.tenant_id === tenantId)) return 'agent';
  return 'user';
};

export function ModularLayout({ children }: ModularLayoutProps) {
  const { user, signOut, currentTenant, availableTenants, switchTenant, profile, refreshProfile, roles } = useAuth();
  // v1.1.0 — Suprime banner legado quando tenant já possui binding integracao_v1.
  // Mesmo cache do PaymentGuard (sem fetch adicional).
  const { data: billingOverview } = useTenantBillingSelfOverview();
  const hasIntegracaoV1Binding =
    !!billingOverview?.has_binding &&
    (billingOverview?.gateway_slug ?? 'uaizy') === 'uaizy';

  // FONTE ÚNICA DE VERDADE para flags de módulo no header (inclui Indicação).
  // `activeModule` é usado abaixo para escolher dinamicamente a sidebar correta.
  const { activeModule, moduleSwitcherProps } = useHeaderModuleFlags();
  
  // Estado do modal de perfil
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  
  // Role do usuário no tenant atual
  const userRole = getUserRole(roles, currentTenant?.id);

  /**
   * Executa logout com redirecionamento via window.location
   */
  const handleLogout = () => {
    signOut(() => {
      window.location.href = '/auth';
    });
  };

  // Determinar qual sidebar renderizar baseado no módulo ativo
  const isCrmActive = activeModule === 'crm';
  const isFinancialActive = activeModule === 'financial';
  const isTasksActive = activeModule === 'tasks';
  const isReferralActive = activeModule === 'referral';
  // Fase 3 — Atividades: garante sidebar própria ao navegar para rotas
  // compartilhadas (Feed Atividades, Agenda, Agendamento Online, Playbook
  // de Vendas) estando no módulo "Atividades".
  const isActivitiesActive = activeModule === 'activities';

  return (
    <PaymentGuard>
    <TooltipProvider>
      <SidebarProvider defaultOpen={true}>
        <div className="app-viewport-min flex w-full">
          {/* Sidebar dinâmica baseada no módulo ativo */}
          <AppShellSidebar />
          
          {/* 
           * CONTENÇÃO FLEXBOX: min-w-0 é OBRIGATÓRIO aqui!
           * Em Flexbox, itens flex têm min-width: auto por padrão, permitindo que
           * conteúdo interno (ex: Kanban com muitas colunas) expanda o container.
           * min-w-0 permite que o item encolha e crie overflow interno.
           */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Header com margens e padding responsivos para mobile */}
            <header className="h-12 sm:h-14 bg-card flex items-center gap-2 px-2 sm:px-4 mx-2 sm:mx-4 md:mx-6 mt-2 sm:mt-4 rounded-lg shadow-sm border">
              {/* Bloco esquerdo: navegação + tenant */}
              <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-shrink-0">
                <SidebarTrigger />
                <TenantSwitcher
                  availableTenants={availableTenants}
                  currentTenant={currentTenant}
                  onSwitch={switchTenant}
                />
                {/* Indicador de Inboxes Desconectadas - apenas xl+ e módulo Conversas */}
                {!isCrmActive && !isFinancialActive && !isTasksActive && !isReferralActive && !isActivitiesActive && (
                  <div className="hidden xl:block">
                    <DisconnectedInboxesIndicator />
                  </div>
                )}
              </div>

              {/* Bloco central: busca global em pílula + botão "+" (estilo Pipedrive) */}
              <HeaderSearchSlot />


              {/* Espaçador no mobile (busca/“+” ficam no menu overflow) */}
              <div className="flex-1 md:hidden" />

              {/* Área central - Banner de Trial - apenas lg+ e módulo Conversas */}
              {!isCrmActive && !isFinancialActive && !isTasksActive && !isReferralActive && !isActivitiesActive && (
                <div className="hidden 2xl:flex items-center gap-4 flex-shrink-0">
                  <TrialBanner
                    trialEndsAt={currentTenant?.trial_ends_at || null}
                    planName={currentTenant?.plan || null}
                    tenantStatus={currentTenant?.status || null}
                    hideForIntegracaoV1={hasIntegracaoV1Binding}
                  />
                </div>
              )}

              
              {/* Ações do Header */}
              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                {/* Menu overflow COMPLETO - visível apenas mobile (<768px) */}
                <div className="md:hidden">
                  <HeaderOverflowMenu {...moduleSwitcherProps} variant="full" />
                </div>

                {/* Menu overflow AÇÕES - visível desktop compacto (768px–1280px) */}
                <div className="hidden md:block xl:hidden">
                  <HeaderOverflowMenu {...moduleSwitcherProps} variant="actions-only" />
                </div>
                
                {/* Status do Agente - apenas xl+ */}
                
                
                {/* Toggle de Tema - apenas xl+ */}
                <div className="hidden xl:block">
                  <ThemeToggle />
                </div>
                
                {/* Notificações - sempre visível */}
                <AppLauncherButton />
                {/* No md+ o "+" já aparece ao lado da busca central */}
                <div className="md:hidden">
                  <QuickCreateButton />
                </div>
                <NotificationBell />
                
                {/* Menu de Usuário - sempre visível */}
                <UserMenu
                  user={user}
                  profile={profile}
                  userRole={userRole}
                  onOpenProfile={() => setProfileDialogOpen(true)}
                  onLogout={handleLogout}
                />
              </div>
            </header>
            
            {/* 
             * Main Content - min-w-0 e overflow-x-hidden garantem que páginas 
             * com conteúdo largo (Kanban, tabelas) não expandam o layout horizontalmente.
             * O scroll horizontal deve ser interno ao componente (ex: ScrollArea).
             */}
            <main className="flex-1 min-w-0 overflow-x-hidden p-3 sm:p-4 md:p-6 bg-muted/30">
              {children}
            </main>
          </div>
        </div>
        
        {/* Modal de Perfil do Usuário */}
        <UserProfileDialog
          open={profileDialogOpen}
          onOpenChange={setProfileDialogOpen}
          userId={user?.id || ''}
          userEmail={user?.email || ''}
          currentName={profile?.full_name || null}
          currentAvatarPath={profile?.avatar_url || null}
          onSuccess={refreshProfile}
        />
        
      </SidebarProvider>
    </TooltipProvider>
    </PaymentGuard>
  );
}
